import React, { useEffect, useMemo, useState } from 'react';
import { Building2, ExternalLink, Search, Filter, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { listCompanies, DEFAULT_BASE_URL } from '../services/api';

const getApiOrigin = () => {
  try {
    const apiBase = DEFAULT_BASE_URL.replace(/\/$/, '');
    const parsed = new URL(apiBase);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return window.location.origin;
  }
};

const getLogoSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/')) return `${getApiOrigin()}${url}`;
  return url;
};

const normalizeWebsiteUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

const PartnersPage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [websiteFilter, setWebsiteFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const data = await listCompanies();
        const list = Array.isArray(data) ? data : (data?.companies || []);
        const active = list.filter(company => company && company.isActive !== false);
        const sorted = active.sort((a, b) => {
          const orderA = Number(a.displayOrder || 0);
          const orderB = Number(b.displayOrder || 0);
          if (orderA !== orderB) return orderA - orderB;
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });
        setCompanies(sorted);
      } catch (e) {
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };
    loadCompanies();
  }, []);

  const filteredCompanies = useMemo(() => {
    let list = [...companies];
    if (search) {
      const query = search.toLowerCase();
      list = list.filter(company =>
        (company.name || '').toLowerCase().includes(query) ||
        (company.tagline || '').toLowerCase().includes(query)
      );
    }
    if (websiteFilter === 'with') {
      list = list.filter(company => company.websiteUrl);
    } else if (websiteFilter === 'without') {
      list = list.filter(company => !company.websiteUrl);
    }
    return list;
  }, [companies, search, websiteFilter]);

  return (
    <div className="min-h-screen bg-white" data-lang={currentLang}>
      <section className="section py-12 partners-hero">
        <div className="container-medium px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="partners-back-btn"
              type="button"
            >
              <ArrowLeft className="h-4 w-4" />
              {getText('partners.back') || 'Back'}
            </button>
            <Link to="/" className="text-sm font-semibold text-primary-600 hover:text-primary-700">
              {getText('partners.homeLink') || 'Go to Home'}
            </Link>
          </div>
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
              {getText('partners.title') || 'Our Partner Companies'}
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {getText('partners.subtitle') || 'Explore the companies tied up with IFM and collaborating across industry initiatives.'}
            </p>
          </div>
        </div>
      </section>

      <section className="section py-12 bg-white">
        <div className="container-medium px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 md:p-6 mb-8">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
              <Filter className="h-4 w-4 text-gray-500" />
              {getText('partners.filters') || 'Filters'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={getText('partners.searchPlaceholder') || 'Search company name or tagline'}
                  className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                />
              </div>
              <div>
                <select
                  value={websiteFilter}
                  onChange={(e) => setWebsiteFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                >
                  <option value="all">{getText('partners.filterAll') || 'All companies'}</option>
                  <option value="with">{getText('partners.filterWithWebsite') || 'With website link'}</option>
                  <option value="without">{getText('partners.filterWithoutWebsite') || 'Without website link'}</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mb-4"></div>
              <p className="text-gray-600">{getText('partners.loading') || 'Loading companies...'}</p>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-10 text-center">
              <p className="text-lg font-semibold text-gray-800 mb-2">
                {getText('partners.emptyTitle') || 'No companies found'}
              </p>
              <p className="text-sm text-gray-600">
                {getText('partners.emptyDesc') || 'Try adjusting your filters or check back later.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCompanies.map((company) => {
                const logoSrc = getLogoSrc(company.logo || company.logoUrl || '');
                const websiteUrl = normalizeWebsiteUrl(company.websiteUrl || '');
                return (
                  <div key={company._id || company.id} className="partners-card flex flex-col">
                    <div className="flex items-center gap-4">
                      <div className="partners-logo">
                        {logoSrc ? (
                          <img
                            src={logoSrc}
                            alt={`${company.name} logo`}
                            className="max-h-10 max-w-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <Building2 className="h-6 w-6 text-gray-300" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
                        {company.tagline && (
                          <p className="text-sm text-gray-600">{company.tagline}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-5 pt-4 border-t border-gray-100">
                      {websiteUrl ? (
                        <a
                          href={websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm font-semibold"
                        >
                          {getText('partners.visitWebsite') || 'Visit Website'}
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : (
                        <span className="text-xs text-gray-500">
                          {getText('partners.noWebsite') || 'Website link not provided'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
